import { getItem } from "~/util/cookies";
import Wasm from "./wasm";
import { IDefPosition, IMapPadding, IMapStyle } from "./interfaces";
import { IWasmStoreGeometry, IWasmStoreMarker } from "./store/interfaces";
import WasmCustomMapMarker from "./custom-items/wasm-custom-map-marker";
import WasmCustomMapGeometry from "./custom-items/wasm-custom-map-geometry";
import { IEvents } from "./custom-items/interfaces";
import { TWasmId } from "~/interfaces";
import { sceneCoordsToPoints } from "~/util/wasm";

type TTodoItem = {
	type: "marker";
	item: WasmCustomMapMarker;
	events?: IEvents;
} | {
	type: "geometry";
	item: WasmCustomMapGeometry;
	events?: IEvents;
}

interface IWasmCustomMap {
	canvas: HTMLCanvasElement;
	canvasClassName: string;
	animTo?: number;
	position?: IDefPosition;
	mapStyle?: IMapStyle;
	onLoad?: () => void;
	onRedraw?: () => void;
}

/** Je nutne, aby mel canvas rodice jako obal span/div */
export default class WasmCustomMap {
	#opts: IWasmCustomMap;

	#cursor: string;

	#parenElem: HTMLElement;

	#wasm: Wasm;

	#loaded: boolean;

	#todoItems: Array<TTodoItem>;

	#markers: Array<WasmCustomMapMarker>;

	#geometries: Array<WasmCustomMapGeometry>;

	// wasm id na elementy
	#mapping: {[key: number]: WasmCustomMapMarker | WasmCustomMapGeometry};

	constructor(opts: IWasmCustomMap) {
		this.#opts = {
			animTo: 400,
			onLoad: () => {},
			onRedraw: () => {},
			...opts,
		};
		this.#parenElem = opts.canvas.parentNode as HTMLElement;
		this.#loaded = false;
		this.#todoItems = [];
		this.#markers = [];
		this.#geometries = [];
		this.#cursor = "";
		this.#mapping = {};
	}

	destroy() {
		for (const marker of this.#markers) {
			marker.destroy();
		}

		for (const geometry of this.#geometries) {
			geometry.destroy();
		}

		this.#markers.length = 0;
		this.#geometries.length = 0;
		this.#wasm.destroy();
	}

	get loaded() {
		return this.#loaded;
	}

	async load() {
		if (!this.#parenElem.isConnected) {
			/* eslint-disable-next-line */
			console.error(`Element is not in the dom!`);

			return;
		}

		this.#wasm = new Wasm({
			parentElem: this.#parenElem as HTMLElement,
			// 1.5 prace, 2 macbook, standard 1; todor
			dpi: 2, //window.devicePixelRatio < 1.5 ? 1 : 2,
			isMobile: Mapy.Util.isMobile(),
			animTo: this.#opts.animTo,
			release: getItem("wasmRelease") === "1",
			canvasQuery: `.${this.#opts.canvasClassName}`,
			position: this.#opts.position,
			mapStyle: this.#opts.mapStyle,
			onMapClick: data => {
				if (data.wasmId) {
					const item = this.#mapping[data.wasmId];

					if (item?.events.onClick) {
						item.events.onClick();
					}
				}
			},
			onDblClick: event => {
				const zoom = this.#wasm.getZoom() + 1;

				this.#wasm.zoomToPixel({ x: event.clientX, y: event.clientY }, zoom);
			},
			onHover: data => {
				let cursor = "";

				if (data.type === "enter" || data.type === "move") {
					if (data.poi || data.wasmId) {
						cursor = "pointer";
					}

					if (data.type === "enter" && data.wasmId) {
						const item = this.#mapping[data.wasmId];

						if (item?.events.onEnter) {
							item.events.onEnter();
						}
					}
				} else if (data.type === "leave" && data.wasmId) {
					const item = this.#mapping[data.wasmId];

					if (item?.events.onLeave) {
						item.events.onLeave();
					}
				}

				this.#setCursor(cursor);
			},
			/* eslint-disable-next-line */
			onContextMenu: (event, poi, wasmId) => {
				//this.#contextMenu(event, poi, wasmId);
			},
			onBestfit: coordinates => {
				const setupData = this.#wasm.mapContent.getMapViewSetupToShowPoints(coordinates.flat(), this.#wasm.module.CoordSystem.Wgs, this.#wasm.module.ShowElementsMode.MoveZoom, 0, 0, 0, 0);

				this.#wasm.mapControl.setMapView(setupData.mapViewSetup);
			},
			onMapRedraw: () => {
				if (!this.#loaded) {
					return;
				}

				this.#opts.onRedraw();
			},
		});
		await this.#wasm.loadProm;
		this.#loaded = true;

		if (this.#todoItems.length > 0) {
			for (const todoItem of this.#todoItems) {
				if (todoItem.type === "marker") {
					todoItem.item.create(this.#wasm, this, todoItem.events);
					this.#markers.push(todoItem.item);
				} else if (todoItem.type === "geometry") {
					todoItem.item.create(this.#wasm, this, todoItem.events);
					this.#geometries.push(todoItem.item);
				}

				this.#addWasmMapping(todoItem.item);
			}
		}

		this.#todoItems.length = 0;
		this.#opts.onLoad();
		this.#opts.onRedraw();
	}

	createMarker(markerData: Partial<IWasmStoreMarker>, events?: IEvents) {
		const marker = new WasmCustomMapMarker(markerData);

		if (this.#loaded) {
			marker.create(this.#wasm, this, events);
			this.#markers.push(marker);
			this.#addWasmMapping(marker);
		} else {
			this.#todoItems.push({
				item: marker,
				type: "marker",
				events,
			});
		}

		return marker;
	}

	createGeometry(geometryData: Partial<IWasmStoreGeometry>, events?: IEvents) {
		const geometry = new WasmCustomMapGeometry(geometryData);

		if (this.#loaded) {
			geometry.create(this.#wasm, this);
			this.#geometries.push(geometry);
			this.#addWasmMapping(geometry);
		} else {
			this.#todoItems.push({
				item: geometry,
				type: "geometry",
				events,
			});
		}

		return geometry;
	}

	// callback z entit
	onDelete(type: "marker" | "geometry", id: number, wasmIds: Array<TWasmId>) {
		this.#todoItems = this.#todoItems.filter(todoItem => todoItem.item.id !== id);

		if (type === "marker") {
			this.#markers = this.#markers.filter(item => item.id !== id);
		} else if (type === "geometry") {
			this.#geometries = this.#geometries.filter(item => item.id !== id);
		}

		for (const wasmId of wasmIds) {
			if (wasmId in this.#mapping) {
				delete this.#mapping[wasmId];
			}
		}
	}

	zoomToCoords({
		coords,
		padding,
		animate,
	}: {
		coords: Array<any>;
		padding?: Partial<IMapPadding>;
		animate?: boolean;
	}) {
		if (!this.#loaded) {
			return;
		}

		const mapPadding: IMapPadding = {
			left: 0,
			bottom: 0,
			right: 0,
			top: 0,
			...padding,
		};
		// @ts-ignore
		const setup = this.#wasm.mapContent.getMapViewSetupToShowPoints(sceneCoordsToPoints(coords), this.#wasm.module.CoordSystem.Wgs, this.#wasm.module.ShowElementsMode.MoveZoom, mapPadding.left, mapPadding.top, mapPadding.right, mapPadding.bottom);

		this.#wasm.mapControl.animToMapView(setup.mapViewSetup, animate ? this.#opts.animTo : 0, this.#wasm.module.EasingType.Linear);
	}

	getZoom() {
		return this.#wasm.getZoom();
	}

	setMapstyle(mapStyle: IMapStyle) {
		this.#wasm.setMapstyle(mapStyle);
	}

	getCenterZoom() {
		return this.#wasm.getCenterZoom();
	}

	setCenterZoomAnim(wgsX: number, wgsY: number, zoom: number, animMs = this.#opts.animTo) {
		this.#wasm.setCenterZoomAnim(wgsX, wgsY, zoom, animMs);
	}

	syncPort() {
		this.#wasm.syncPort();
	}

	getViewport() {
		return this.#wasm.getViewport();
	}

	#setCursor(cursor: string) {
		if (this.#cursor !== cursor) {
			this.#parenElem.style.cursor = cursor || "default";
			this.#cursor = cursor;
		}
	}

	#addWasmMapping(item: WasmCustomMapMarker | WasmCustomMapGeometry) {
		const ids = item.wasmIds;

		for (const id of ids) {
			this.#mapping[id] = item;
		}
	}
}
